import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./Spinner.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";

/**
 * Props for the Spinner component
 * @extends ComponentPropsWithoutRef<"span">
 */
export type SpinnerProps = ComponentPropsWithoutRef<"span"> & {
  /**
   * When true, the spinner inherits the current text color instead of using the default primary background color
   */
  inherit?: boolean;

  /**
   * The size of the spinner.
   * @default medium
   */
  size?: "small" | "medium" | "large";
};

/**
 * A loading spinner component that displays an animated circular indicator.
 *
 * The Spinner component provides a visual indication that content is loading or processing.
 * It renders as a circular animation that continuously rotates to show activity.
 *
 * Features:
 * - Animated circular loading indicator
 * - Configurable color inheritance
 * - Accessible with proper ARIA attributes
 * - Customizable styling through className and style props
 * - Forwarded ref support
 *
 * @example
 * <Spinner />
 *
 * <Spinner inherit />
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(
  (props, ref) => {
    const { className, inherit, size = "medium", ...rest } = props;

    const containerRef = useRef<HTMLSpanElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    // Merge the forwarded ref with our internal ref
    const mergedRef = useMergeRefs([containerRef, ref]);

    useEffect(() => {
      let rafId: number;
      let lastWidth = 0;

      const updateWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          if (width !== lastWidth) {
            setContainerWidth(width);
            lastWidth = width;
          }
        }
      };

      const continuousUpdate = () => {
        updateWidth();
        rafId = requestAnimationFrame(continuousUpdate);
      };

      // Initial measurement
      updateWidth();

      // Set up ResizeObserver for efficiency when not animating
      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      // Also run continuous updates to catch CSS transitions
      rafId = requestAnimationFrame(continuousUpdate);

      return () => {
        resizeObserver.disconnect();
        cancelAnimationFrame(rafId);
      };
    }, []);

    const classNames = cx(styles["Spinner"], className, {
      [styles["Inherit"]]: inherit,
      [styles["size-small"]]: size === "small",
      [styles["size-medium"]]: size === "medium",
      [styles["size-large"]]: size === "large",
    });

    // Calculate strokeWidth using sqrt((width - 8px) / 2) - use direct value for smooth animation
    const strokeWidth =
      containerWidth > 0 ? Math.sqrt((containerWidth - 8) / 2) : 2;

    // Keep radius consistent - let the stroke width be the variable part
    // Use a fixed radius that gives good proportions
    const radius = 10;

    return (
      <span
        data-anv="spinner"
        className={classNames}
        ref={mergedRef}
        role="status"
        {...rest}
      >
        <svg
          className={styles["Svg"]}
          width="100%"
          height="100%"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            className={styles["Track"]}
            cx="12"
            cy="12"
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
            stroke="currentColor"
            shapeRendering="geometricPrecision"
          />
          <path
            className={styles["Arc"]}
            d={`M${12 + radius} 12a${radius} ${radius} 0 1 0-${radius * 2} 0`}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            stroke="currentColor"
            shapeRendering="geometricPrecision"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 12 12"
              to="360 12 12"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </span>
    );
  },
);

Spinner.displayName = "Spinner";
